import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useToast } from "../../components/ui/Toast";
import { useParams, useNavigate } from "react-router-dom";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Plus,
  Play,
  FileArrowUp,
  FilePdf,
  Notebook,
  Exam,
  BookOpen,
  Flask,
  DotsThree,
  CalendarBlank,
  Sparkle,
  WifiSlash,
  ArrowClockwise,
  CaretDown,
  MagnifyingGlass,
  SortAscending,
  Trash,
  ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { useHomeworkStore } from "../../stores/homework";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "../../components/ui/Dropdown";
import type {
  AssignmentListItem,
  AssignmentType,
} from "../../types/homework";

const EASE = [0.2, 0.8, 0.2, 1] as const;

const TYPE_CONFIG: Record<
  AssignmentType,
  { icon: typeof Exam; label: string; color: string }
> = {
  essay: {
    icon: Notebook,
    label: "Essay",
    color: "var(--color-lever-deep)",
  },
  pset: {
    icon: Exam,
    label: "Problem Set",
    color: "var(--color-lever-orange)",
  },
  lab: { icon: Flask, label: "Lab Report", color: "var(--color-signal-green)" },
  reading: {
    icon: BookOpen,
    label: "Reading",
    color: "var(--color-cool-azure)",
  },
  other: { icon: DotsThree, label: "Other", color: "var(--text-muted)" },
};

const STATUS_STYLES = {
  not_started: {
    bg: "bg-[var(--bg-surface)]",
    text: "text-[var(--text-muted)]",
    label: "Not Started",
  },
  in_progress: {
    bg: "bg-[var(--color-cool-azure)]/10",
    text: "text-[var(--color-cool-azure)]",
    label: "In Progress",
  },
  completed: {
    bg: "bg-[var(--color-signal-green)]/10",
    text: "text-[var(--color-signal-green)]",
    label: "Completed",
  },
};

function AssignmentRow({
  assignment,
  onStart,
  onOpen,
  onReopen,
  onDelete,
  index,
  isStarting,
}: {
  assignment: AssignmentListItem;
  onStart: () => void;
  onOpen: () => void;
  onReopen: () => void;
  onDelete: () => void;
  index: number;
  isStarting?: boolean;
}) {
  const type = TYPE_CONFIG[assignment.assignment_type];
  const status = STATUS_STYLES[assignment.status];
  const Icon = type.icon;

  const dueDate = assignment.due_date
    ? new Date(assignment.due_date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.04, ease: EASE }}
      className={cn(
        "group flex items-center gap-3 px-4 py-3",
        "rounded-[var(--radius-lg)] border border-[var(--border-hairline)]",
        "bg-[var(--bg-surface)]",
        "hover:bg-[var(--bg-surface-hover)] hover:border-[var(--border-subtle)]",
        "transition-all duration-150",
      )}
    >
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-md)]"
        style={{ background: `color-mix(in srgb, ${type.color} 10%, transparent)` }}
      >
        <Icon size={17} style={{ color: type.color }} />
      </div>

      <div className="flex-1 min-w-0">
        <h4 className="text-[13px] font-semibold text-[var(--text-primary)] truncate">
          {assignment.title}
        </h4>
        <div className="mt-0.5 flex items-center gap-2 text-[12px] text-[var(--text-muted)]">
          <span>{type.label}</span>
          {dueDate && (
            <>
              <span className="opacity-40">/</span>
              <span className="flex items-center gap-0.5">
                <CalendarBlank size={11} />
                {dueDate}
              </span>
            </>
          )}
        </div>
      </div>

      <span
        className={cn(
          "rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide",
          status.bg,
          status.text,
        )}
      >
        {status.label}
      </span>

      {assignment.status === "not_started" ? (
        <button
          onClick={onStart}
          disabled={isStarting}
          className={cn(
            "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[13px] font-semibold",
            "bg-[var(--text-primary)] text-[var(--text-inverse)]",
            "hover:opacity-85 active:scale-[0.98]",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "transition-all duration-150",
          )}
        >
          <Play size={13} weight="fill" />
          {isStarting ? "Starting..." : "Start"}
        </button>
      ) : assignment.status === "in_progress" ? (
        <button
          onClick={onOpen}
          className={cn(
            "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[13px] font-medium",
            "border border-[var(--border-default)] text-[var(--text-primary)]",
            "hover:bg-[var(--bg-surface-hover)]",
            "transition-colors duration-150",
          )}
        >
          Open
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={onOpen}
            className={cn(
              "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[13px] font-medium",
              "border border-[var(--border-default)] text-[var(--text-primary)]",
              "hover:bg-[var(--bg-surface-hover)]",
              "transition-colors duration-150",
            )}
          >
            Open
          </button>
          <button
            onClick={onReopen}
            className={cn(
              "flex items-center gap-1.5 rounded-[var(--radius-md)] px-2.5 py-1.5 text-[12px] font-medium",
              "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
              "hover:bg-[var(--bg-surface-hover)]",
              "transition-colors duration-150",
            )}
            title="Mark as in progress"
          >
            <ArrowCounterClockwise size={13} />
            Reopen
          </button>
        </div>
      )}

      <motion.button
        onClick={onDelete}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.85 }}
        className={cn(
          "flex items-center justify-center shrink-0 rounded-[var(--radius-sm)] p-1.5",
          "text-[var(--text-muted)] opacity-0 group-hover:opacity-100",
          "hover:bg-[var(--color-soft-red)]/10 hover:text-[var(--color-soft-red)]",
          "transition-all duration-100",
        )}
        aria-label="Delete assignment"
      >
        <Trash size={13} />
      </motion.button>
    </motion.div>
  );
}

function CreateAssignmentDialog({
  open,
  onClose,
  courseId,
}: {
  open: boolean;
  onClose: () => void;
  courseId: string;
}) {
  const [title, setTitle] = useState("");
  const [assignmentType, setAssignmentType] = useState<AssignmentType>("other");
  const [dueDate, setDueDate] = useState("");
  const { createAssignment } = useHomeworkStore();

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = useCallback(async () => {
    if (!title.trim() || creating) return;
    setCreating(true);
    setError(null);
    try {
      await createAssignment(courseId, title.trim(), {
        assignment_type: assignmentType,
        due_date: dueDate || undefined,
      });
      setTitle("");
      setAssignmentType("other");
      setDueDate("");
      onClose();
    } catch {
      setError("Failed to create assignment. Please try again.");
    } finally {
      setCreating(false);
    }
  }, [title, assignmentType, dueDate, courseId, createAssignment, onClose, creating]);

  if (!open) return null;

  const inputClasses = cn(
    "w-full rounded-[var(--radius-md)] border border-[var(--border-subtle)]",
    "bg-[var(--bg-base)] px-3 py-2 text-[13px] text-[var(--text-primary)]",
    "placeholder:text-[var(--text-muted)]",
    "outline-none focus:border-[var(--color-lever-orange)]",
    "transition-colors duration-150",
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className={cn(
              "relative z-10 w-full max-w-lg",
              "rounded-[var(--radius-xl)] border border-[var(--border-subtle)]",
              "bg-[var(--bg-surface)] shadow-[var(--shadow-xl)]",
              "p-6",
            )}
            initial={{ scale: 0.95, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 12 }}
            transition={{ duration: 0.25, ease: EASE }}
          >
            <h2 className="text-base font-semibold text-[var(--text-primary)]" style={{ fontFamily: "var(--font-display)" }}>
              New Assignment
            </h2>

            <div className="mt-4 flex flex-col gap-3">
              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Problem Set 3"
                  autoFocus
                  className={inputClasses}
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {(
                    Object.entries(TYPE_CONFIG) as [
                      AssignmentType,
                      (typeof TYPE_CONFIG)[AssignmentType],
                    ][]
                  ).map(([key, cfg]) => (
                    <button
                      key={key}
                      onClick={() => setAssignmentType(key)}
                      className={cn(
                        "flex items-center gap-2 rounded-[var(--radius-md)] border px-3 py-2 text-[13px]",
                        "transition-colors duration-150",
                        assignmentType === key
                          ? "border-[var(--color-lever-orange)] bg-[var(--color-lever-orange)]/8 text-[var(--color-lever-orange)] font-medium"
                          : "border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-surface-hover)]",
                      )}
                    >
                      <cfg.icon size={15} />
                      {cfg.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">Due Date</label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className={inputClasses}
                />
              </div>
            </div>

            {error && (
              <p className="mt-3 text-[12px] text-[var(--color-soft-red)]">{error}</p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={onClose}
                className={cn(
                  "rounded-[var(--radius-md)] px-3 py-1.5 text-[13px] font-medium text-[var(--text-secondary)]",
                  "hover:bg-[var(--bg-surface-hover)]",
                  "transition-colors duration-150",
                )}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!title.trim() || creating}
                className={cn(
                  "rounded-[var(--radius-md)] px-4 py-1.5 text-[13px] font-semibold",
                  "bg-[var(--text-primary)] text-[var(--text-inverse)]",
                  "hover:opacity-85 active:scale-[0.98]",
                  "disabled:opacity-30 disabled:cursor-not-allowed",
                  "transition-all duration-150",
                )}
              >
                {creating ? "Creating..." : "Create"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function MaterialsSection({ courseId }: { courseId: string }) {
  const { materials, fetchMaterials, uploadMaterial, deleteMaterial, parseSyllabus, isLoading } =
    useHomeworkStore();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadType, setUploadType] = useState<string>("reference");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchMaterials(courseId);
  }, [courseId, fetchMaterials]);

  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      setUploading(true);
      try {
        await uploadMaterial(courseId, Array.from(files), uploadType);
      } finally {
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
      }
    },
    [courseId, uploadMaterial, uploadType],
  );

  const handleDelete = useCallback(
    async (materialId: string) => {
      await deleteMaterial(courseId, materialId);
    },
    [courseId, deleteMaterial],
  );

  const chunkStatusLabel = (status?: string) => {
    switch (status) {
      case "processing": return "Indexing...";
      case "ready": return "Indexed";
      case "failed": return "Index failed";
      default: return "Pending";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: EASE }}
      className="flex flex-col gap-4"
    >
      <div className="flex items-center gap-3">
        <Dropdown>
          <DropdownTrigger
            className={cn(
              "flex items-center gap-1.5 rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--bg-base)]",
              "px-3 py-2 text-[13px] text-[var(--text-secondary)]",
              "hover:border-[var(--border-default)] hover:bg-[var(--bg-surface-hover)]",
              "transition-colors duration-200",
            )}
          >
            {uploadType.charAt(0).toUpperCase() + uploadType.slice(1)}
            <CaretDown size={12} className="text-[var(--text-muted)]" />
          </DropdownTrigger>
          <DropdownMenu>
            {(["syllabus", "handout", "reading", "reference"] as const).map((type) => (
              <DropdownItem
                key={type}
                onClick={() => setUploadType(type)}
                className={cn(
                  uploadType === type && "bg-[var(--bg-surface-hover)] font-medium",
                )}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className={cn(
            "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[13px] font-medium",
            "border border-[var(--border-default)] text-[var(--text-primary)]",
            "hover:bg-[var(--bg-surface-hover)]",
            "disabled:opacity-40",
            "transition-colors duration-150",
          )}
        >
          <FileArrowUp size={15} />
          {uploading ? "Uploading..." : "Upload"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.txt,.md,.tex,.bib,.csv"
          multiple
          className="hidden"
          onChange={handleUpload}
        />
        <button
          onClick={() => parseSyllabus(courseId)}
          disabled={isLoading}
          className={cn(
            "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[13px] font-semibold",
            "bg-[var(--text-primary)] text-[var(--text-inverse)]",
            "hover:opacity-85 active:scale-[0.98]",
            "disabled:opacity-30",
            "transition-all duration-150",
          )}
        >
          <Sparkle size={15} />
          Parse Syllabus
        </button>
      </div>

      {materials.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--bg-surface)]">
            <FilePdf size={20} className="text-[var(--text-muted)]" />
          </div>
          <p className="mt-3 text-[14px] text-[var(--text-muted)]">
            No materials uploaded yet
          </p>
          <p className="mt-1 text-[12px] text-[var(--text-muted)]">
            Upload PDFs, handouts, or readings to get started.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {materials.map((mat) => (
            <div
              key={mat.id}
              className={cn(
                "group flex items-center gap-3 rounded-[var(--radius-lg)] px-4 py-3",
                "bg-[var(--bg-surface-solid)] border border-[var(--border-hairline)]",
                "hover:shadow-[var(--shadow-sm)]",
                "transition-all duration-200",
              )}
            >
              <FilePdf
                size={17}
                className="shrink-0 text-[var(--color-soft-red)]"
              />
              <span className="flex-1 truncate text-[14px] text-[var(--text-primary)]">
                {mat.filename}
              </span>
              <span
                className={cn(
                  "text-[10px] px-1.5 py-0.5 rounded-[var(--radius-sm)]",
                  mat.chunk_status === "ready" && "bg-[var(--color-soft-green)]/15 text-[var(--color-soft-green)]",
                  mat.chunk_status === "processing" && "bg-[var(--color-soft-yellow)]/15 text-[var(--color-soft-yellow)]",
                  mat.chunk_status === "failed" && "bg-[var(--color-soft-red)]/15 text-[var(--color-soft-red)]",
                  (!mat.chunk_status || mat.chunk_status === "pending") && "bg-[var(--bg-surface)] text-[var(--text-muted)]",
                )}
              >
                {chunkStatusLabel(mat.chunk_status)}
              </span>
              <span className="lever-label">{mat.material_type}</span>
              <button
                onClick={() => handleDelete(mat.id)}
                className={cn(
                  "shrink-0 rounded-[var(--radius-sm)] p-1",
                  "text-[var(--text-muted)] opacity-0 group-hover:opacity-100",
                  "hover:bg-[var(--bg-surface-hover)] hover:text-[var(--color-soft-red)]",
                  "transition-all duration-150",
                )}
                title="Delete material"
              >
                <Trash size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

function SyllabusView() {
  const course = useHomeworkStore((s) => s.currentCourse);
  const structured = course?.syllabus_structured;

  if (!structured) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        className="flex flex-col items-center justify-center py-16 text-center"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-lever-deep)]/8">
          <Sparkle size={22} className="text-[var(--color-lever-deep)]" />
        </div>
        <p className="mt-4 text-[15px] font-medium text-[var(--text-primary)]">
          No syllabus parsed yet
        </p>
        <p className="mt-1.5 max-w-xs text-[13px] leading-relaxed text-[var(--text-muted)]">
          Upload a syllabus in the Materials tab and click "Parse Syllabus" to auto-detect assignments and structure.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: EASE }}
      className="flex flex-col gap-8"
    >
      {structured.grading?.components && (
        <div>
          <h4 className="lever-label mb-3">Grading</h4>
          <div className="flex flex-wrap gap-2">
            {structured.grading.components.map((c, i) => (
              <span
                key={i}
                className={cn(
                  "rounded-full px-3.5 py-1.5 text-[13px] font-medium",
                  "bg-[var(--color-lever-orange)]/8 text-[var(--color-lever-orange)]",
                  "border border-[var(--color-lever-orange)]/15",
                )}
              >
                {c.name}: {c.weight}
              </span>
            ))}
          </div>
        </div>
      )}

      {structured.schedule && structured.schedule.length > 0 && (
        <div>
          <h4 className="lever-label mb-3">Schedule</h4>
          <div className="flex flex-col gap-0.5">
            {structured.schedule.slice(0, 15).map((item, i) => (
              <div
                key={i}
                className={cn(
                  "flex gap-4 rounded-[var(--radius-md)] px-3 py-2.5 text-[13px]",
                  i % 2 === 0 ? "bg-[var(--bg-surface)]/50" : "",
                )}
              >
                <span className="w-14 shrink-0 font-semibold text-[var(--text-muted)]">
                  Wk {item.week}
                </span>
                <span className="text-[var(--text-primary)]">{item.topic}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {structured.policies && structured.policies.length > 0 && (
        <div>
          <h4 className="lever-label mb-3">Policies</h4>
          <ul className="flex flex-col gap-2 text-[13px] leading-relaxed text-[var(--text-secondary)]">
            {structured.policies.map((p, i) => (
              <li key={i} className="flex gap-3">
                <span className="shrink-0 mt-1.5 h-1 w-1 rounded-full bg-[var(--text-muted)]" />
                {p}
              </li>
            ))}
          </ul>
        </div>
      )}
    </motion.div>
  );
}

type AssignmentSortKey = "title" | "due_date" | "status" | "type";
const ASSIGNMENT_SORT_OPTIONS: { key: AssignmentSortKey; label: string }[] = [
  { key: "due_date", label: "Due date" },
  { key: "title", label: "Title" },
  { key: "status", label: "Status" },
  { key: "type", label: "Type" },
];

const STATUS_ORDER = { not_started: 0, in_progress: 1, completed: 2 };

function AssignmentsTab({
  assignments,
  onShowCreate,
  onStart,
  onOpen,
  onReopen,
  onDelete,
  startingId,
  startError,
}: {
  assignments: AssignmentListItem[];
  onShowCreate: () => void;
  onStart: (id: string) => void;
  onOpen: (id: string) => void;
  onReopen: (id: string) => void;
  onDelete: (id: string) => void;
  startingId: string | null;
  startError: string | null;
}) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<AssignmentSortKey>("due_date");
  const [sortOpen, setSortOpen] = useState(false);

  const filtered = useMemo(() => {
    let list = [...assignments];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.assignment_type.toLowerCase().includes(q),
      );
    }
    list.sort((a, b) => {
      switch (sortKey) {
        case "title":
          return a.title.localeCompare(b.title);
        case "status":
          return STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
        case "type":
          return a.assignment_type.localeCompare(b.assignment_type);
        case "due_date":
        default: {
          const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
          const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
          return da - db;
        }
      }
    });
    return list;
  }, [assignments, search, sortKey]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass
            size={13}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search assignments..."
            className={cn(
              "w-full rounded-[var(--radius-md)] border border-[var(--border-hairline)]",
              "bg-[var(--bg-base)] pl-8 pr-3 py-1.5 text-[12px] text-[var(--text-primary)]",
              "placeholder:text-[var(--text-muted)] outline-none focus:border-[var(--border-default)]",
              "transition-colors duration-100",
            )}
          />
        </div>
        <div className="relative">
          <button
            onClick={() => setSortOpen(!sortOpen)}
            className={cn(
              "flex items-center gap-1.5 rounded-[var(--radius-md)] px-2.5 py-1.5 text-[11px] font-medium",
              "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-hover)]",
              "border border-[var(--border-hairline)] transition-colors duration-100",
            )}
          >
            <SortAscending size={12} weight="bold" />
            {ASSIGNMENT_SORT_OPTIONS.find((o) => o.key === sortKey)?.label ?? "Sort"}
          </button>
          {sortOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setSortOpen(false)} />
              <div
                className={cn(
                  "absolute right-0 top-full z-50 mt-1 min-w-[140px] overflow-hidden",
                  "rounded-[var(--radius-lg)] border border-[var(--border-hairline)]",
                  "bg-[var(--bg-surface)] shadow-[var(--shadow-lg)] backdrop-blur-[var(--blur-lg)] py-1",
                )}
              >
                {ASSIGNMENT_SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => { setSortKey(opt.key); setSortOpen(false); }}
                    className={cn(
                      "flex w-full items-center px-3 py-1.5 text-[11px]",
                      "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75",
                      opt.key === sortKey
                        ? "text-[var(--text-primary)] font-semibold"
                        : "text-[var(--text-secondary)]",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="flex-1" />
        {startError && (
          <p className="text-[12px] text-[var(--color-soft-red)]">{startError}</p>
        )}
        <button
          onClick={onShowCreate}
          className={cn(
            "flex items-center gap-1.5 rounded-[var(--radius-md)] px-3.5 py-2 text-[13px] font-semibold",
            "bg-[var(--text-primary)] text-[var(--text-inverse)]",
            "hover:opacity-85 active:scale-[0.98]",
            "transition-all duration-150",
          )}
        >
          <Plus size={15} />
          Add Assignment
        </button>
      </div>

      {assignments.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="flex flex-col items-center justify-center py-16 text-center"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-lever-orange)]/8">
            <Exam size={22} className="text-[var(--color-lever-orange)]" />
          </div>
          <p className="mt-4 text-[15px] font-medium text-[var(--text-primary)]">
            No assignments yet
          </p>
          <p className="mt-1.5 max-w-xs text-[13px] leading-relaxed text-[var(--text-muted)]">
            Add one manually or parse your syllabus to auto-detect them.
          </p>
        </motion.div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center py-12 text-center">
          <MagnifyingGlass size={18} className="text-[var(--text-muted)]" />
          <p className="mt-2 text-[13px] text-[var(--text-muted)]">No assignments matching "{search}"</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map((a, i) => (
            <AssignmentRow
              key={a.id}
              assignment={a}
              index={i}
              onStart={() => onStart(a.id)}
              onOpen={() => onOpen(a.id)}
              onReopen={() => onReopen(a.id)}
              onDelete={() => onDelete(a.id)}
              isStarting={startingId === a.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type Tab = "assignments" | "syllabus" | "materials";

export function ClassDetailPage() {
  useDocumentTitle("Class");
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const {
    currentCourse,
    assignments,
    fetchError,
    fetchCourse,
    fetchAssignments,
    startAssignment,
    reopenAssignment,
  } = useHomeworkStore();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<Tab>("assignments");
  const [showCreateAssignment, setShowCreateAssignment] = useState(false);
  const [startingId, setStartingId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  useEffect(() => {
    if (!classId) return;
    fetchCourse(classId);
    fetchAssignments(classId);
  }, [classId, fetchCourse, fetchAssignments]);

  const handleStart = useCallback(
    async (assignmentId: string) => {
      if (startingId) return;
      setStartingId(assignmentId);
      setStartError(null);
      try {
        await startAssignment(assignmentId);
        navigate(`/classes/${classId}/assignments/${assignmentId}`);
      } catch {
        setStartError("Failed to start assignment. Please try again.");
        setStartingId(null);
      }
    },
    [classId, startAssignment, navigate, startingId],
  );

  const handleOpen = useCallback(
    (assignmentId: string) => {
      navigate(`/classes/${classId}/assignments/${assignmentId}`);
    },
    [classId, navigate],
  );

  const handleReopen = useCallback(
    async (assignmentId: string) => {
      try {
        await reopenAssignment(assignmentId);
        toast.success("Assignment reopened", "Status changed back to in progress.");
      } catch {
        toast.error("Reopen failed", "Could not reopen the assignment.");
      }
    },
    [reopenAssignment, toast],
  );

  if (fetchError && !currentCourse) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/classes")}
            className={cn(
              "flex items-center justify-center rounded-[var(--radius-md)] p-1.5",
              "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
              "hover:bg-[var(--bg-surface-hover)]",
              "transition-colors duration-150",
            )}
          >
            <ArrowLeft size={16} />
          </button>
          <span className="text-[13px] font-medium text-[var(--text-muted)]">Back to classes</span>
        </div>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-soft-red)]/10">
            <WifiSlash size={22} className="text-[var(--color-soft-red)]" />
          </div>
          <p className="mt-3 text-[14px] font-medium text-[var(--text-primary)]">
            Can't reach the server
          </p>
          <p className="mt-1.5 max-w-sm text-[13px] text-[var(--text-muted)]">
            {fetchError}
          </p>
          <button
            onClick={() => {
              if (classId) {
                fetchCourse(classId);
                fetchAssignments(classId);
              }
            }}
            className={cn(
              "mt-4 flex items-center gap-1.5 rounded-[var(--radius-md)] px-3 py-1.5 text-[13px] font-medium",
              "border border-[var(--border-default)] text-[var(--text-primary)]",
              "hover:bg-[var(--bg-surface-hover)]",
              "transition-colors duration-150",
            )}
          >
            <ArrowClockwise size={14} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!currentCourse) {
    return (
      <div className="flex items-center justify-center p-16">
        <div className="flex flex-col items-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-[var(--color-lever-orange)] border-t-transparent" />
          <span className="text-[13px] text-[var(--text-muted)]">Loading...</span>
        </div>
      </div>
    );
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: "assignments", label: "Assignments" },
    { key: "syllabus", label: "Syllabus" },
    { key: "materials", label: "Materials" },
  ];

  return (
    <div className="p-6 max-w-5xl">
      {/* ── Header ── */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate("/classes")}
          className={cn(
            "flex items-center justify-center rounded-[var(--radius-md)] p-1.5",
            "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
            "hover:bg-[var(--bg-surface-hover)]",
            "transition-colors duration-150",
          )}
        >
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1
            className="text-lg font-semibold text-[var(--text-primary)] tracking-[-0.03em]"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {currentCourse.name}
          </h1>
          <div className="flex items-center gap-2 text-[12px] text-[var(--text-muted)]">
            {currentCourse.instructor && <span>{currentCourse.instructor}</span>}
            {currentCourse.instructor && currentCourse.term && <span className="opacity-40">·</span>}
            {currentCourse.term && <span>{currentCourse.term}</span>}
          </div>
        </div>
      </div>

      <div>
        {/* ── Tabs ── */}
        <div className="mb-5 flex items-center gap-0.5 border-b border-[var(--border-subtle)]">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "relative px-3 py-2.5 text-[13px] font-medium transition-colors duration-150",
                activeTab === tab.key
                  ? "text-[var(--text-primary)]"
                  : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
              )}
            >
              {tab.label}
              {activeTab === tab.key && (
                <motion.div
                  layoutId="tab-indicator"
                  className="absolute bottom-0 left-0 right-0 h-[2px] lever-gradient-accent rounded-full"
                  transition={{ duration: 0.25, ease: EASE }}
                />
              )}
            </button>
          ))}
        </div>

        {/* ── Content ── */}
        {activeTab === "assignments" && (
          <AssignmentsTab
            assignments={assignments}
            onShowCreate={() => setShowCreateAssignment(true)}
            onStart={handleStart}
            onOpen={handleOpen}
            onReopen={handleReopen}
            onDelete={(id) => setDeleteTargetId(id)}
            startingId={startingId}
            startError={startError}
          />
        )}

        {activeTab === "syllabus" && <SyllabusView />}

        {activeTab === "materials" && classId && (
          <MaterialsSection courseId={classId} />
        )}
      </div>

      {classId && (
        <CreateAssignmentDialog
          open={showCreateAssignment}
          onClose={() => setShowCreateAssignment(false)}
          courseId={classId}
        />
      )}

      {deleteTargetId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteTargetId(null)}>
          <div
            className="mx-4 w-full max-w-sm rounded-[var(--radius-xl)] border border-[var(--border-default)] bg-[var(--bg-primary)] p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">Delete assignment?</h3>
            <p className="mt-2 text-[13px] text-[var(--text-muted)]">
              This action cannot be undone. The assignment and all associated work will be permanently deleted.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setDeleteTargetId(null)}
                className="rounded-[var(--radius-md)] px-3 py-1.5 text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)] transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const id = deleteTargetId;
                  try {
                    await useHomeworkStore.getState().deleteAssignment(classId!, id);
                    setDeleteTargetId(null);
                    toast.success("Deleted", "Assignment deleted successfully.");
                  } catch {
                    toast.error("Delete failed", "Could not delete assignment.");
                  }
                }}
                className="rounded-[var(--radius-md)] bg-[var(--color-soft-red)] px-3 py-1.5 text-[13px] font-medium text-white hover:opacity-90 transition-opacity duration-150"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
